import React, { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { classNames } from '../helpers/functions';

// Global counter for z-index stacking
let modalCounter = 0;
const getNextZIndex = () => {
	modalCounter += 1;
	return 100 + modalCounter * 10; // Start at 110, increment by 10
};
const releaseZIndex = () => {
	modalCounter = Math.max(0, modalCounter - 1);
};

/**
 * StackableModal - A modal component that properly supports stacking/nesting
 *
 * Unlike the standard Modal component, this one:
 * - Renders via portal to document.body
 * - Manages z-index automatically for proper stacking
 * - Handles focus without conflicting with parent modals
 * - Uses click-based close instead of HeadlessUI Dialog's focus trap
 */
const StackableModal = ({
	open,
	onClose,
	title,
	children,
	size = 'lg',
	showCloseIcon = true,
	preventBackdropClose = false,
	initialFocusRef,
	sidebar,
	dialogClassName = '',
}) => {
	const [zIndex, setZIndex] = useState(100);
	const modalRef = useRef(null);
	const previousActiveElement = useRef(null);

	// Manage z-index on mount/unmount
	useEffect(() => {
		if (open) {
			setZIndex(getNextZIndex());
			previousActiveElement.current = document.activeElement;

			// Prevent body scroll
			document.body.style.overflow = 'hidden';

			return () => {
				releaseZIndex();
				document.body.style.overflow = '';

				// Restore focus to previous element
				if (previousActiveElement.current) {
					previousActiveElement.current.focus?.();
				}
			};
		}
	}, [open]);

	// Handle initial focus
	useEffect(() => {
		if (open) {
			const timer = setTimeout(() => {
				if (initialFocusRef?.current) {
					initialFocusRef.current.focus();
				} else {
					// Focus the modal container or first focusable element
					const firstFocusable = modalRef.current?.querySelector(
						'input, button, textarea, select, [tabindex]:not([tabindex="-1"])',
					);
					firstFocusable?.focus();
				}
			}, 50);
			return () => clearTimeout(timer);
		}
	}, [open, initialFocusRef]);

	// Handle escape key
	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && !preventBackdropClose) {
				onClose?.();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open, onClose, preventBackdropClose]);

	// Handle backdrop click
	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget && !preventBackdropClose) {
			onClose?.();
		}
	};

	const sizeClasses = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
		'2xl': 'max-w-2xl',
		'3xl': 'max-w-3xl',
		'4xl': 'max-w-4xl',
		'5xl': 'max-w-5xl',
		'6xl': 'max-w-6xl',
		'7xl': 'max-w-7xl',
		full: 'max-w-full h-screen',
	};

	if (!open) return null;

	const modalContent = (
		<Transition show={open} as={Fragment}>
			<div
				className="fixed inset-0"
				style={{ zIndex }}
				role="dialog"
				aria-modal="true"
			>
				{/* Backdrop */}
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div
						className="fixed inset-0 bg-gray-500/75 transition-opacity"
						onClick={handleBackdropClick}
					/>
				</Transition.Child>

				{/* Modal container */}
				<div
					className="fixed inset-0 overflow-y-auto"
					onClick={handleBackdropClick}
				>
					<div className="flex min-h-full md:items-center md:justify-center md:p-4 md:text-center sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div
								ref={modalRef}
								className={classNames(
									'relative transform bg-white text-left shadow-xl transition-all w-full rounded-lg',
									sizeClasses[size] || sizeClasses.lg,
									size === 'full'
										? 'h-screen'
										: 'sm:my-8 px-4 pt-5 pb-4 sm:p-6',
									dialogClassName,
								)}
								onClick={(e) => e.stopPropagation()}
							>
								{/* Close button */}
								{showCloseIcon && (
									<div className="absolute top-0 right-0 pt-4 pr-4">
										<button
											type="button"
											className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
											onClick={onClose}
										>
											<span className="sr-only">Close</span>
											<XMarkIcon className="h-6 w-6" aria-hidden="true" />
										</button>
									</div>
								)}

								{/* Title */}
								{title && (
									<div className="sm:flex sm:items-start">
										<div className="mt-3 sm:mt-0 sm:mx-4 text-center sm:text-left flex-1">
											<h3 className="text-lg font-medium leading-6 text-gray-900">
												{title}
											</h3>
										</div>
									</div>
								)}

								{/* Content */}
								{sidebar ? (
									<div className={classNames('flex', title ? 'mt-4' : '')}>
										<div className="flex-1 text-left sm:mx-4 overflow-y-auto max-h-[70vh]">
											{children}
										</div>
										<div className="w-64 border-l border-gray-200 bg-gray-50 rounded-r-lg -mr-6 -my-6 ml-4">
											{sidebar}
										</div>
									</div>
								) : (
									<div
										className={classNames(
											'sm:text-left',
											title ? 'mt-4' : '',
											size !== 'full' && 'sm:mx-4',
										)}
									>
										{children}
									</div>
								)}
							</div>
						</Transition.Child>
					</div>
				</div>
			</div>
		</Transition>
	);

	// Render to body via portal
	return createPortal(modalContent, document.body);
};

export default StackableModal;
